import queryString from 'qs';
import hybrid from '@zoom/hybrid-jssdk';
import cookie from 'js-cookie';
import { omit, trim } from 'lodash';

import { translation } from '@/plugins/i18n';
import { THEME } from '@/views/checkout/constant.js';
import { pingServerSession } from '@/apis/index.js';
import { logger } from '@/utils/log';
import { getOnlinePlanName } from '@/common/product';
import { MODAL_QUERY_PARAMS, OPC_HREF_SYNC_QUERY_PARAMS, URL_PAGE_TYPE_KEY } from '@/common/constants.js';
import { updateHrefWithQueryParams } from '@/utils';
import { pageConfig } from '@/common/service/page-config';

import store from '@/store/index.js';
// cross-origin support
import { shouldUseCrossOrigin } from '@/utils/crossOriginHelper';

const modalURLLimitQueries = [...MODAL_QUERY_PARAMS, '_'];

const ModalGlobalEventChannels = {
  SHOW_MODAL_CLOSE: 'opc-show-modal-close',
};

export const DEFAULT_MODAL_WIDTH = 688;
export const DEFAULT_MODAL_HEIGHT = 715;
export const DEFAULT_MODAL_SIMPLIFY_HEIGHT = 420;

export const PLAN_SELECTION_DIALOG_WIDTH = 920;
export const PLAN_SELECTION_DIALOG_HEIGHT = 820;

// use this.$context.on to listen global event 'opc-show-modal-close', params is boolean, change this.showModalClose
export const listenModalCloseButtonShow = (cb) => {
  this.$context.$on(ModalGlobalEventChannels.SHOW_MODAL_CLOSE, (showModalClose) => {
    cb(showModalClose);
  });
};

export const triggerModalCloseButtonShow = (show) => {
  this.$context.$emit(ModalGlobalEventChannels.SHOW_MODAL_CLOSE, show);
};

const opcModalPostMessage = (params) => {
  // * use postMessage to post message to parent window
  if (window?.parent) {
    logger.log('modal dialog post message: ', params);

    window.parent.postMessage(params, '*');
  }
};

export const MODAL_CHECKOUT_MESSAGE_HOOK_MAP = {
  close: 'mc_close',
  successCheckout: 'mc_success',
  openNewTab: 'mc_openNewTab', // *
  replaceCurrentPage: 'mc_replaceCurrentPage',
  showClose: 'mc_showClose',
  hiddenClose: 'mc_hiddenClose',
  showLoading: 'mc_showLoading',
  hiddenLoading: 'mc_hiddenLoading',
  sendClose: 'mc_send_close',
  switchAccount: 'mc_switch_account',
  resize: 'mc_resize_modal',
  business: 'mc_business_event',
  // Cross-origin communication message types
  navigate: 'mc_navigate',
  reloadParent: 'mc_reload_parent',
  // Generic cross-origin communication
  safeCall: 'mc_safe_call',
  safeSet: 'mc_safe_set',
  safeGetCall: 'mc_safe_get_call',
  safeGetResponse: 'mc_safe_get_response',
};

export const closeModal = () => {
  if (modalHybrid.isInWebview && window === window?.parent) {
    modalHybrid.closeModal();
  } else {
    opcModalPostMessage({
      type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.close,
    });
  }
};

export const resizeModalDialogIniFrame = ({ height = DEFAULT_MODAL_SIMPLIFY_HEIGHT, capToViewport = false, width = null }) => {
  let nextHeight = height;
  // When resizing the modal iframe container, avoid requesting a height larger than the
  // (parent) viewport. Accessing `window.parent` may throw in cross-origin iframes, so
  // we best-effort read parent viewport height and fall back to the iframe viewport.
  // This cap is opt-in to avoid unintended behavioral changes for other call sites.
  if (capToViewport && typeof height === 'number') {
    let viewportHeight = 0;
    try {
      if (window?.parent && window.parent !== window && typeof window.parent.innerHeight === 'number') {
        viewportHeight = window.parent.innerHeight;
      }
    } catch (e) {
      viewportHeight = 0;
    }
    if (!viewportHeight && typeof window?.innerHeight === 'number') {
      viewportHeight = window.innerHeight;
    }
    const maxHeight = viewportHeight ? Math.floor(viewportHeight) : 0;
    if (maxHeight > 0) {
      nextHeight = Math.min(height, maxHeight);
    }
  }
  const containerStyle = {
    height: typeof nextHeight === 'number' ? `${nextHeight}px` : nextHeight,
  };
  if (width) {
    containerStyle.width = typeof width === 'number' ? `${width}px` : width;
  }
  opcModalPostMessage({
    type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.resize,
    containerStyle,
  });
};

export const notifyCancelEvent = () => {
  opcModalPostMessage({
    type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.business,
    actionType: 'cancel_plan',
  });
};

export const notifyPageLoadedEvent = () => {
  opcModalPostMessage({
    type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.business,
    actionType: 'page_loaded',
  });
};

export const notifySwitchAccount = () => {
  opcModalPostMessage({
    type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.switchAccount,
  });
};

// * export function replaceCurrentPage, params is url which is the target url
export const replaceCurrentPage = (url) => {
  logger.log('modal dialog replaceCurrentPage: ', url);
  opcModalPostMessage({
    type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.replaceCurrentPage,
    link: url,
  });
};

export const openUrlInNewTab = (url) => {
  logger.log('modal dialog openUrlInNewTab: ', url);
  opcModalPostMessage({
    type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.openNewTab,
    link: url,
  });
};

const ANCHOR_NEW_TAB_TARGETS = new Set(['_blank']);

let modalIframeTrickyPatchRegistered = false;
let modalIframeTrickyPatchHandler = null;

const shouldOpenAnchorInNewTab = (anchorElement) => {
  if (!anchorElement || anchorElement.tagName !== 'A') {
    return false;
  }
  const target = anchorElement.getAttribute('target');
  return typeof target === 'string' && ANCHOR_NEW_TAB_TARGETS.has(target.toLowerCase());
};

export const navigateParentWindowTo = (url, closeModalFlag = true) => {
  logger.log('modal dialog navigateParentWindowTo: ', url);
  if (modalHybrid.isInWebview) {
    modalHybrid.openLinkInBrowser(url);
    if (closeModalFlag) {
      closeModal();
    }
  } else {
    replaceCurrentPage(url);
  }
};

/**
 * Navigate to target page with automatic origin handling
 * Automatically handles modal/non-modal scenarios and cross-origin support
 *
 * @param {string} link - Target URL (can be relative or absolute)
 * @param {boolean} ensureOrigin - Auto-prepend origin for relative URLs (default: true)
 *
 * @example
 * navigateToTargetPage('/billing'); // Auto-prepends origin in modal context
 * navigateToTargetPage('https://zoom.us'); // Uses as-is for absolute URLs
 */
export const navigateToTargetPage = (link, ensureOrigin = true) => {
  // Auto-prepend origin for relative URLs
  let targetUrl = link;
  if (ensureOrigin && link && !link.startsWith('http') && !link.startsWith('//')) {
    // Ensure single leading slash
    const normalizedPath = link.startsWith('/') ? link : `/${link}`;
    targetUrl = `${window.location.origin}${normalizedPath}`;
  }

  // cross-origin support
  if (!shouldUseCrossOrigin()) {
    // Same-origin or non-embedded mode, use original logic
    if (window.$context?.is?.modal) {
      navigateParentWindowTo(targetUrl);
    } else {
      window.location.href = targetUrl;
    }
  } else {
    // Cross-origin mode, use safe communication
    // Use the same message type as replaceCurrentPage
    if (modalHybrid.isInWebview && window !== window?.parent) {
      navigateParentWindowTo(targetUrl);
    } else {
      opcModalPostMessage({
        type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.navigate,
        link: targetUrl,
      });
    }
  }
};

const urlPreHandler = (url) => {
  if (modalHybrid.isInWebview) {
    // add forceSignIn for OPC url in client env
    if (url.includes('/opc/buy')) {
      return `${url}&forceSignIn=true`;
    }
  }
  return url;
};

export const navigateParentWindowToFullOPC = (closeModal = true) => {
  const url = new URL(location.href);
  const queries = queryString.parse(url.search.split('?')[1]);
  const newOPCURL = `${url.pathname}?${queryString.stringify(omit(queries, modalURLLimitQueries))}`;
  navigateParentWindowTo(newOPCURL, closeModal);
};

export const navigateParentWindowToFullOPCCheckout = () => {
  const url = new URL(location.href);
  const queries = queryString.parse(url.search.split('?')[1]);
  const newOPCURL = `/opc/buy/checkout?${queryString.stringify(omit(queries, modalURLLimitQueries))}`;
  navigateParentWindowTo(newOPCURL);
};

// export funtion which name is noticeCheckoutSuccess
// use opcModalPostmessage to post message to parent window, which params is a object with type 'mc_success''
// @param {Array} productData - Array of product information objects, each containing planName, planCode, category, parentCategory
export const noticeParentWindowCheckoutSuccess = (productData = []) => {
  logger.log('modal dialog noticeCheckoutSuccess', productData);
  opcModalPostMessage({
    type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.successCheckout,
    productData,
  });
};

// export funtion which name is show modalDialogCloseButton
// use opcModalPostmessage to post message to parent window, which params is a object with type 'mc_showClose'
export const showModalDialogCloseButton = () => {
  logger.log('modal dialog showModalDialogCloseButton');
  opcModalPostMessage({
    type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.showClose,
  });
};

export const hideModalDialogCloseButton = () => {
  logger.log('modal dialog hideModalDialogCloseButton');
  opcModalPostMessage({
    type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.hiddenClose,
  });
};

export const showiFrameContainerLoading = () => {
  logger.log('modal dialog showiFrameContainerLoading');
  opcModalPostMessage({
    type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.showLoading,
  });
};

export const closeiFrameContainerLoading = () => {
  if (MODAL_CHECKOUT_MESSAGE_HOOK_MAP.hiddenLoading) {
    logger.log('modal dialog closeiFrameContainerLoading');
    opcModalPostMessage({
      type: MODAL_CHECKOUT_MESSAGE_HOOK_MAP.hiddenLoading,
    });
  }
};

export const navigateCurrentWindowTo = (url, { replace = false } = {}) => {
  if (!url || typeof url !== 'string') return;
  if (window?.$context?.is?.modal && pageIsInIframe()) {
    showiFrameContainerLoading();
  }
  if (replace) {
    window.location.replace(url);
  } else {
    window.location.href = url;
  }
};

// * tricky patch for navigation in OPC
export const modalIframeTrickyPatch = () => {
  if (modalIframeTrickyPatchRegistered) {
    return;
  }
  if (!document?.body) {
    return;
  }

  modalIframeTrickyPatchHandler = function (e) {
    var target = e.target,
      targetUrl = '',
      openInNewTab = false;
    if (!target || typeof target.closest !== 'function') {
      return;
    }
    if (target.closest('.zbo-message-box__btns .zbo-button--primary')) {
      var btnText = target.textContent.trim();
      var returnHomePageBtnText = translation('billing.return_home');
      if (btnText.includes(returnHomePageBtnText)) {
        targetUrl = '/';
      }
    } else if (target.closest('p.zbo-message__content') && target?.href?.indexOf('/billing/report') > -1) {
      targetUrl = '/billing/report';
    } else if (target.closest('.zbo-message-box__content a') && target?.href?.indexOf('/billing/payment') > -1) {
      targetUrl = '/billing/payment';
    } else if (target?.tagName === 'A') {
      // Intercept <a> tag navigation
      const href = target?.getAttribute?.('href');

      // Validate href: must be a valid navigation URL
      // Skip: empty, javascript:, hash-only, non-navigation protocols, excluded patterns
      if (
        href &&
        href.trim() !== '' &&
        href !== '#' &&
        href.trim() !== '#' &&
        !href.toLowerCase().startsWith('javascript:') &&
        !/^(mailto|tel|sms|data|file|ftp):/i.test(href) &&
        !href.includes('pageType=modal') &&
        !href.includes('opc/buy')
      ) {
        targetUrl = href;
        openInNewTab = shouldOpenAnchorInNewTab(target);
      }
    }

    if (targetUrl) {
      e.preventDefault();
      e.stopPropagation();
      if (modalHybrid.isInWebview) {
        modalHybrid.openLinkInBrowser(targetUrl);
        // modalHybrid.closeModal();
      } else {
        if (openInNewTab) {
          openUrlInNewTab(targetUrl);
        } else {
          replaceCurrentPage(targetUrl);
        }
      }
    }
  };

  document.body.addEventListener(
    'click',
    modalIframeTrickyPatchHandler,
    true,
  );
  modalIframeTrickyPatchRegistered = true;
};

export const removeModalIframeTrickyPatch = () => {
  if (!modalIframeTrickyPatchRegistered || !document?.body || !modalIframeTrickyPatchHandler) {
    return;
  }
  document.body.removeEventListener('click', modalIframeTrickyPatchHandler, true);
  modalIframeTrickyPatchRegistered = false;
  modalIframeTrickyPatchHandler = null;
};

export const toggleTheme = function (isDark) {
  const definedTheme = isDark ? THEME.DARK : THEME.LIGHT;
  // window.themeMode = definedTheme;
  store.commit('setClientWebViewTheme', definedTheme);
  if (isDark) {
    if (!hasClass(document.body, 'zbo-theme--dark')) {
      addClass(document.body, 'zbo-theme--dark');
    }
    removeContainerbackgroundWhtie();
  } else {
    if (hasClass(document.body, 'zbo-theme--dark')) {
      removeClass(document.body, 'zbo-theme--dark');
    }
    setContainerbackgroundWhtie();
  }
};

const MATCH_MEDIA_DARK_QUERY = '(prefers-color-scheme: dark)';

// * set client webview theme
export function setClientTheme() {
  const matchMedia = window.matchMedia;
  const darkTheme = matchMedia && matchMedia(MATCH_MEDIA_DARK_QUERY);

  toggleTheme(darkTheme?.matches);
}

const setContainerbackgroundWhtie = (queries = 'html,body') => {
  [...document?.querySelectorAll(queries)].forEach((x) => (x.style.background = 'var(--zoom-color-bg-default, #FFFFFF)'));
};

const removeContainerbackgroundWhtie = (queries = 'html,body') => {
  [...document?.querySelectorAll(queries)].forEach((x) => (x.style.background = null));
};

export const setModalCheckoutParams = () => {
  const { pageFrom, entrySource } = pageConfig;
  const modalCheckoutParams = window.sessionStorage.getItem('modal_checkout_params');

  // Only patch missing fields and keep other cached params untouched.
  let parsedParams = {};
  try {
    parsedParams = JSON.parse(modalCheckoutParams) || {};
  } catch (e) {
    parsedParams = {};
  }

  let needUpdate = false;
  if (!parsedParams.pageFrom && pageFrom) {
    parsedParams.pageFrom = pageFrom;
    needUpdate = true;
  }
  if (!parsedParams.entrySource && entrySource) {
    parsedParams.entrySource = entrySource;
    needUpdate = true;
  }

  if (needUpdate) {
    window.sessionStorage.setItem('modal_checkout_params', JSON.stringify(parsedParams));
  }
};

export const getModalCheckoutParams = () => {
  const modalCheckoutParams = window.sessionStorage.getItem('modal_checkout_params');
  if (!modalCheckoutParams) {
    return {};
  }
  try {
    return JSON.parse(modalCheckoutParams) || {};
  } catch (e) {
    logger.warn('Failed to parse modal_checkout_params:', e);
    return {};
  }
};

/**
 * Get modal checkout configs from sessionStorage
 * ⚠️⚠️⚠️ Warning: For cross-origin scenarios, this solution is not applicable.
 *
 * @returns {Object|null} Parsed config object or null if failed
 */
export const getModalCheckoutConfigs = () => {
  try {
    const modalCheckoutConfigs = window.sessionStorage.getItem('modal_checkout_configs');
    if (modalCheckoutConfigs) {
      return JSON.parse(modalCheckoutConfigs);
    }
  } catch (e) {
    logger.warn('Failed to parse modal_checkout_configs:', e);
  }
  return null;
};

// Watch theme changed dark | light
export const watchThemeChange = (debug = false) => {
  const config = getModalCheckoutConfigs();
  // TODO: get from url query params
  const enableDarkTheme = config?.options?.enableDarkTheme || false;
  // * if not in webview and not enableDarkTheme and is production, do not watch theme change
  // * in development, we can watch theme change in browser
  // * if support the theme change watch in browser, we can just romove the if condition here
  if (!modalHybrid.isInWebview && !enableDarkTheme && process.env.NODE_ENV === 'production') {
    return;
  }

  const matchMedia = window.matchMedia;
  const darkTheme = matchMedia && matchMedia(MATCH_MEDIA_DARK_QUERY);

  if (!darkTheme.matches) {
    setContainerbackgroundWhtie();
  }

  if (debug) {
    toggleTheme(true);
  } else {
    toggleTheme(darkTheme.matches);
  }

  try {
    darkTheme?.addEventListener('change', (event) => {
      logger.log('watchThemeChange=>addEventListener=>change: ', event);
      toggleTheme(event.matches);
    });
  } catch (e) {
    // For low safari
    try {
      darkTheme?.addListener(function (event) {
        logger.log('watchThemeChange=>addListener: ', event);
        toggleTheme(event.matches);
      });
    } catch (e) {
      logger.error('matchMedia not work, can not watchThemeChange.', e);
    }
  }
};

export function hasClass(el, cls) {
  if (!el || !cls) return false;
  if (cls.indexOf(' ') !== -1) throw new Error('className should not contain space.');
  if (el.classList) {
    return el.classList.contains(cls);
  } else {
    return (' ' + el.className + ' ').indexOf(' ' + cls + ' ') > -1;
  }
}

export function addClass(el, cls) {
  if (!el) return;
  let curClass = el.className;
  const classes = (cls || '').split(' ');

  for (let i = 0, j = classes.length; i < j; i++) {
    const clsName = classes[i];
    if (!clsName) continue;

    if (el.classList) {
      el.classList.add(clsName);
    } else if (!hasClass(el, clsName)) {
      curClass += ' ' + clsName;
    }
  }
  if (!el.classList) {
    el.className = curClass;
  }
}

export function removeClass(el, cls) {
  if (!el || !cls) return;
  const classes = cls.split(' ');
  let curClass = ' ' + el.className + ' ';

  for (let i = 0, j = classes.length; i < j; i++) {
    const clsName = classes[i];
    if (!clsName) continue;

    if (el.classList) {
      el.classList.remove(clsName);
    } else if (hasClass(el, clsName)) {
      curClass = curClass.replace(' ' + clsName + ' ', ' ');
    }
  }
  if (!el.classList) {
    el.className = trim(curClass);
  }
}

class ModalHybrid {
  constructor() {
    this.isInWebview = hybrid.webview.isInWebview();

    // * add modalHybrid to window in dev
    logger.log('modal client: process.env.NODE_ENV: ', process.env.NODE_ENV);
    if (process.env.NODE_ENV === 'development') {
      logger.log('modal client: add modal Hybrid to window in dev');
      window.modalHybrid = this;
    }

    if (this.isInWebview) {
      this.bridgeInstance = hybrid.bridge.getInstance({ config: { softRegisterBridge: true, iframeSupport: true, iframeAllowedOrigin: ['*'] } });
      this.hybrid = hybrid;
    } else {
      logger.log('modal client: not in webview, ModalHybrid not init');
    }
  }
  registerNotificationHandler(callback) {
    if (this.isInWebview) {
      this.bridgeInstance.registerNotificationHandler(callback);
    } else {
      logger.log(`modal client: not in webview, ModalHybrid not init, registerNotificationHandler`);
    }
  }
  registerSpecificNotificationHandler(channel, callback) {
    if (this.isInWebview) {
      this.bridgeInstance.registerSpecificNotificationHandler(channel, callback);
    } else {
      logger.log(
        `modal client: not in webview, ModalHybrid not init, registerSpecificNotificationHandler ${channel} not work`,
      );
    }
  }
  getSystemParam() {
    return this.hybrid.platform.getSystemParam();
  }
  openLinkInBrowserDirectly(url) {
    // postJsMessage directly to resolve the call for 'platform.getSystemParam' and 3s delay in mac client
    this.bridgeInstance.postJsMessage({
      functionName: 'common.openLinkInBrowser',
      params: {
        url,
      },
    });
  }
  openLinkInBrowser(url) {
    if (this.isInWebview) {
      logger.log('modal hybrid openLinkInBrowser', url);
      try {
        new URL(url);
        // this.hybrid.common.openLinkInBrowser(urlPreHandler(url));
        this.openLinkInBrowserDirectly(urlPreHandler(url));
      } catch (error) {
        // * full path
        const hostUrl = new URL(location.href);
        // this.hybrid.common.openLinkInBrowser(urlPreHandler(`${hostUrl.protocol}//${hostUrl.host}${url}`));
        this.openLinkInBrowserDirectly(urlPreHandler(`${hostUrl.protocol}//${hostUrl.host}${url}`));
      }
    } else {
      logger.log('modal client openLinkInBrowser error, is not in client env', url);
    }
  }
  postMessage(data, needResponse = false) {
    this.bridgeInstance.postJsMessage({
      apiName: 'postMessage',
      data,
      needResponse,
    });
  }
  sendLoginMessageToClient(clientLoginParams) {
    logger.log('modal client: sendLoginMessageToClient: ');
    if (this.isInWebview) {
      this.postMessage({
        functionName: 'LoginAfterActivated',
        params: clientLoginParams,
      });
    }
  }
  notifyFreeTrialStatus(type = 0) {
    logger.log('modal client: notifyFreeTrialStatus: ', type);
    if (this.isInWebview) {
      this.postMessage({
        functionName: 'notifyFreeTrialStatus',
        params: { type },
      });
    }
  }
  resizeWebview(width = DEFAULT_MODAL_WIDTH, height = DEFAULT_MODAL_HEIGHT) {
    logger.log('modal client: resizeWebview: ', width, height);
    if (this.isInWebview) {
      this.postMessage({
        functionName: 'OnSizeNotify',
        params: {
          width,
          height,
        },
      });
    } else {
      logger.log('modal client: is not in webview, can not resizeWebview');
    }
  }
  closeModal() {
    logger.log('modal client => closeWebviewModal: ');
    if (this.isInWebview) {
      this.postMessage({
        functionName: 'CloseDialog',
      });
    } else {
      logger.log('modal client: is not in webview, can not CloseDialog');
    }
  }
}

export const modalHybrid = new ModalHybrid();

const FREE_TRIAL_INFO_SESSIN_STORAGE_KEY = 'SIGNUP/__FREE_TRIAL_INFO__';

export let getFreeTrialInfoFromSignUpActivePage = () => {
  const freeTrialInfo = JSON.parse(sessionStorage.getItem(FREE_TRIAL_INFO_SESSIN_STORAGE_KEY)) || {};
  // const {
  //   clientLoginParams,
  //   returnUrl
  // } = freeTrialInfo
  logger.log('modal=>getFreeTrialInfoFromSignUpActivePage: ', freeTrialInfo);
  getFreeTrialInfoFromSignUpActivePage = () => {
    logger.log('modal=>getFreeTrialInfoFromSignUpActivePage, override: ', freeTrialInfo);
    return freeTrialInfo;
  };
  return freeTrialInfo;
};

export const sendLoginMessageToClient = () => {
  const { clientLoginParams } = getFreeTrialInfoFromSignUpActivePage();

  modalHybrid.sendLoginMessageToClient(clientLoginParams);
};

export const pingServerInterval = (interval = 60 * 60 * 1000) => {
  setInterval(pingServerSession, interval);
};

export const afterPurchaseSuccessHandlerInModalCheckout = () => {
  // Get selected plans from store and build product data array
  const { getSelectedPlans } = store.getters;
  const productData = (getSelectedPlans || []).map((plan) => ({
    planName: getOnlinePlanName(plan) || translation(plan?.planNameKey) || translation(plan?.typeNameKey) || '',
    planCode: plan?.plan_code || '',
    category: plan?.category || '',
    parentCategory: plan?.parent_category || '',
  }));
  noticeParentWindowCheckoutSuccess(productData);
  showiFrameContainerLoading();
  showModalDialogCloseButton();
};

export const pageIsInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return false;
  }
};

export const jumpToTargetPageForDiffType = (link, queryParams = OPC_HREF_SYNC_QUERY_PARAMS) => {
  if (!link || typeof link !== 'string') {
    return;
  }
  if (window?.$context?.is?.modal) {
    navigateCurrentWindowTo(
      updateHrefWithQueryParams(link, queryParams, { [URL_PAGE_TYPE_KEY]: 'modal' }),
      { replace: false }
    );
  } else {
    navigateCurrentWindowTo(link, { replace: false });
  }
};

const compareZoomClientVersions = (version1, version2) => {
  const v1 = version1.split('.');
  const v2 = version2.split('.');

  for (let i = 0; i < Math.max(v1.length, v2.length); i++) {
    const num1 = parseInt(v1[i]) || 0;
    const num2 = parseInt(v2[i]) || 0;

    if (num1 > num2) {
      return 1;
    } else if (num1 < num2) {
      return -1;
    }
  }

  return 0;
};

const OS_ENUMS = {
  mac: 'Mac',
  win: 'Win',
  unknown: 'Unknown',
};

const getClientWebViewOS = (userAgent = navigator.userAgent) => {
  if (/Mac OS X/i.test(userAgent)) {
    return OS_ENUMS.mac;
  } else if (/Windows NT/i.test(userAgent)) {
    return OS_ENUMS.win;
  } else {
    return OS_ENUMS.unknown;
  }
};

const generateBasic2proURLForBrowser = (clientVersion = null) => {
  var urlParams = new URLSearchParams(window.location.search);
  // urlParams.set('forceSignIn', true);
  urlParams.set('itm_platform', getClientWebViewOS().toLowerCase());
  urlParams.set('itm_lang', window.campaignFlags.locale);
  urlParams.set('itm_country', window.campaignFlags.currentCountry);
  if (getClientWebViewOS() === OS_ENUMS.win) {
    urlParams.set('itm_version', clientVersion);
  }
  for (const key of modalURLLimitQueries) {
    urlParams.delete(key);
  }
  const queryString = urlParams.toString();
  const baseUrl = window.location.origin + window.location.pathname;
  return `${baseUrl}?${queryString}`;
}

export const precheckZoomClientEnvironmentForBasic2Pro = () => {
  const os = getClientWebViewOS();
  if (os === OS_ENUMS.mac) {
    if (!cookie.get('zak')) {
      logger.warn('modal client: precheckZoomClientEnvironment => mac os no zak cookie');
      navigateParentWindowTo(generateBasic2proURLForBrowser());
    }
  } else if (os === OS_ENUMS.win) {
    modalHybrid
      .getSystemParam()
      .then((systemParam) => {
        logger.log('modal client: precheckZoomClientEnvironment => win os systemParam', systemParam);
        if (compareZoomClientVersions(systemParam.clientVersion.trim(), '6.3.0') < 0) {
          logger.warn('modal client: precheckZoomClientEnvironment => win os clientVersion < 6.3.0');
          navigateParentWindowTo(generateBasic2proURLForBrowser(systemParam.clientVersion.trim()));
        }
      })
      .catch((err) => {
        logger.error('getSystemParam error', err);
        navigateParentWindowTo(generateBasic2proURLForBrowser());
      });
  } else {
    logger.warn('modal client: precheckZoomClientEnvironment => getClientWebViewOS unknown error', os);
  }
};
